import React, { useState } from 'react'; // Import useState
import { NavLink, useLocation } from 'react-router-dom'; // Import useLocation
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth for role checking
import './Sidebar.css';
import logo from '../../assets/ezOCM_Logo.png';
import { FaProjectDiagram, FaClipboardList, FaBullhorn, FaUsers, FaCog, FaChevronDown, FaChevronRight, FaLayerGroup, FaUserCog, FaRegListAlt, FaCreditCard, FaChartBar } from 'react-icons/fa'; // Added FaCog, Chevrons, FaLayerGroup, FaUserCog, FaRegListAlt, FaCreditCard, FaChartBar

function Sidebar({ isOpen, onClose }) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isCommunicationOpen, setIsCommunicationOpen] = useState(false);
  const location = useLocation(); // Get current location
  const { user } = useAuth(); // Get current user for role checking

  // Role checking constants
  const userRole = user?.role;
  const isAdmin = userRole === 'Admin' || userRole === 'System Administrator';
  const isChangeManager = userRole === 'Change Manager';

  // Handle menu item click - close mobile sidebar
  const handleMenuItemClick = () => {
    // Close mobile sidebar when a menu item is clicked
    if (window.innerWidth <= 768) {
      onClose();
    }
  };

  // Determine if the current path is under /settings
  const isSettingsActive = location.pathname.startsWith('/settings');
  // Determine if the current path is under /communication
  const isCommunicationActive = location.pathname.startsWith('/key-messages') || 
                               location.pathname.startsWith('/plans') || 
                               location.pathname.startsWith('/communication-dashboard');

  // Toggle Settings submenu
  const toggleSettings = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  // Toggle Communication submenu
  const toggleCommunication = () => {
    setIsCommunicationOpen(!isCommunicationOpen);
  };

  // Keep settings open if a settings sub-page is active
  React.useEffect(() => {
    if (isSettingsActive) {
      setIsSettingsOpen(true);
    }
    // Optional: Close if navigating away from settings entirely
    // else {
    //   setIsSettingsOpen(false);
    // }
  }, [isSettingsActive]);

  // Keep communication open if a communication sub-page is active
  React.useEffect(() => {
    if (isCommunicationActive) {
      setIsCommunicationOpen(true);
    }
  }, [isCommunicationActive]);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="sidebar-overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      
      <aside className={`app-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Logo at the top of the sidebar */}
        <div className="sidebar-logo-container">
          <img src={logo} alt="ezOCM Logo" className="sidebar-logo" />
        </div>
        <nav>
          <ul>
            {/* Admin and Change Manager sections */}
            {(isAdmin || isChangeManager) && (
              <>
                <li>
                  <NavLink to="/reports" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                    <FaChartBar /> Reports
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/projects" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                    <FaProjectDiagram /> Projects
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/assessments" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                    <FaClipboardList /> Assessments
                  </NavLink>
                </li>
              </>
            )}

            {/* My Assessments - Available to all users - positioned after Assessments */}
            <li>
              <NavLink to="/my-assessments" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                <FaRegListAlt /> My Assessments
              </NavLink>
            </li>

            {/* Stakeholders - positioned after My Assessments */}
            {(isAdmin || isChangeManager) && (
              <li>
                <NavLink to="/stakeholders" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                  <FaUsers /> Stakeholders
                </NavLink>
              </li>
            )}

            {/* Continue with admin/change manager sections */}
            {(isAdmin || isChangeManager) && (
              <>
                {/* Communication with Submenu */}
                <li className={isCommunicationOpen ? 'submenu-open' : ''}>
                  <button 
                    type="button" 
                    onClick={toggleCommunication} 
                    className={`sidebar-button-item ${isCommunicationActive ? "active" : ""}`}
                    style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <FaBullhorn /> Communication
                    <span className="submenu-toggle-icon">
                      {isCommunicationOpen ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                  </button>
                  {isCommunicationOpen && (
                    <ul className="submenu">
                      <li>
                        <NavLink to="/communication-dashboard" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                          <FaChartBar /> Dashboard
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/key-messages" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                          <FaClipboardList /> Key Messages
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/plans" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                          <FaBullhorn /> Plans
                        </NavLink>
                      </li>
                    </ul>
                  )}
                </li>

                {/* Settings section - available to admins and change managers */}
                <li className={isSettingsOpen ? 'submenu-open' : ''}>
                  <button 
                    type="button" 
                    onClick={toggleSettings} 
                    className={`sidebar-button-item ${isSettingsActive ? "active" : ""}`}
                    style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  >
                    <FaCog /> Settings
                    <span className="submenu-toggle-icon">
                      {isSettingsOpen ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                  </button>
                  {isSettingsOpen && (
                    <ul className="submenu">
                      <li>
                        <NavLink to="/settings/employees" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                          <FaUserCog /> Employee Management
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/settings/groups" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                          <FaLayerGroup /> Groups
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/settings/assessment-templates" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                          <FaClipboardList /> Assessment Management
                        </NavLink>
                      </li>
                      {/* Subscription - Change Managers and Admin */}
                      {(userRole === 'Change Manager' || isAdmin) && (
                        <li>
                          <NavLink to="/settings/subscription" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                            <FaCreditCard /> Subscription
                          </NavLink>
                        </li>
                      )}
                      {/* Subscription Plans - Admin only */}
                      {isAdmin && (
                        <>
                          <li>
                            <NavLink to="/settings/plan-management" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                              <FaCreditCard /> Subscription Plans
                            </NavLink>
                          </li>
                          <li>
                            <NavLink to="/settings/trial-management" className={({ isActive }) => isActive ? "active" : ""} onClick={handleMenuItemClick}>
                              <FaChartBar /> Trial Management
                            </NavLink>
                          </li>
                        </>
                      )}
                    </ul>
                  )}
                </li>
              </>
            )}
            
          </ul>
        </nav>
      </aside>
    </>
  );
}

export default Sidebar;

